import React from "react";
import {
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  DateField,
  BooleanField,
  ReferenceField,
  ReferenceManyField,
  Datagrid,
  EditButton,
  Button,
  Pagination,
  NumberField,
  FunctionField,
  RaRecord,
  useTranslate,
  usePermissions,
  useRecordContext,
  BulkDeleteWithConfirmButton,
} from "react-admin";
import { Link } from "react-router-dom";
import { Add as AddIcon } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { SYSTEM_ADMINISTRATOR } from "../authProvider/roles";

import { Customer } from "../types";

const AddNewDomainButton = () => {
  const record = useRecordContext();
  return (
    <Button
      variant="contained"
      component={Link}
      to={{
        pathname: "/Domain/create",
        search: `?customerId=${record?.id}`,
      }}
      label="Domain hinzufügen"
      sx={{ marginTop: "1em", marginBottom: "1em" }}
    >
      <AddIcon />
    </Button>
  );
};

const Aside = () => {
  const record = useRecordContext<Customer>();
  return (
    <div style={{ width: 200, margin: "1em" }}>
      {record && (
        <>
          <Typography variant="h6">{record.name}</Typography>

          <Typography variant="caption" display="block">
            CRM-Id
          </Typography>
          <Typography variant="body2" gutterBottom>
            {record.crmId ? (
              <a
                href={`https://purplemine.networkteam.com/contacts/${record.crmId}`}
              >
                #{record.crmId}
              </a>
            ) : (
              "-"
            )}
          </Typography>

          <Typography variant="caption" display="block">
            Kontaktperson
          </Typography>
          <Typography variant="body2" gutterBottom>
            {record.contactPerson}
          </Typography>

          <Typography variant="caption" display="block">
            Erstellt
          </Typography>
          <Typography variant="body2" gutterBottom>
            <DateField source="createdAt" record={record} showTime />
          </Typography>

          <Typography variant="caption" display="block">
            Letzte Änderung
          </Typography>
          <Typography variant="body2" gutterBottom>
            <DateField source="updatedAt" record={record} showTime />
          </Typography>
        </>
      )}
    </div>
  );
};

const Title = () => {
  const record = useRecordContext<Customer>();
  return (
    <span>
      Kunde <strong>{record?.name}</strong>
    </span>
  );
};

const CustomerShow = () => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  return (
    <Show title={<Title />} aside={<Aside />}>
      <TabbedShowLayout>
        <Tab label="Domains">
          <ReferenceManyField
            label={false}
            pagination={<Pagination />}
            reference="Domain"
            target="customerId"
            sort={{ field: "unicodeName", order: "ASC" }}
          >
            <Datagrid
              rowClick="show"
              bulkActionButtons={
                <BulkDeleteWithConfirmButton mutationMode="pessimistic" />
              }
            >
              <TextField source="unicodeName" />
              <BooleanField source="isMainDomain" />
              <BooleanField source="mailEnabled" />
              <ReferenceField
                source="mainDomainId"
                reference="Domain"
                sortable={false}
              >
                <TextField source="domain" />
              </ReferenceField>
              <FunctionField
                source="managedBy"
                render={(record?: RaRecord) => {
                  const value = record?.managedBy;
                  return value
                    ? translate(`resources.Domain.domainManager.${value}`)
                    : "-";
                }}
              />
              {permissions === SYSTEM_ADMINISTRATOR && <EditButton />}
            </Datagrid>
          </ReferenceManyField>
          {permissions === SYSTEM_ADMINISTRATOR && <AddNewDomainButton />}
        </Tab>
        {permissions === SYSTEM_ADMINISTRATOR && (
          <Tab label="Service-Buchungen">
            <ReferenceManyField
              label={false}
              pagination={<Pagination />}
              reference="ServiceBooking"
              target="customerId"
              sort={{ field: "bookedFrom", order: "DESC" }}
            >
              <Datagrid
                rowClick="show"
                bulkActionButtons={
                  <BulkDeleteWithConfirmButton mutationMode="pessimistic" />
                }
              >
                <NumberField source="amount" label="Anzahl" />
                <ReferenceField
                  source="serviceTemplateId"
                  reference="ServiceTemplate"
                  sortable={false}
                  link={false}
                >
                  <TextField source="name" />
                </ReferenceField>
                <TextField source="comment" />

                <NumberField
                  source="serviceTemplate.monthlyCost"
                  label="mtl. Preis"
                  options={{ style: "currency", currency: "EUR" }}
                />

                <DateField source="bookedFrom" />
                <DateField source="invoicedUntil" />
                <DateField source="canceledOn" />
              </Datagrid>
            </ReferenceManyField>
          </Tab>
        )}
      </TabbedShowLayout>
    </Show>
  );
};

export default CustomerShow;
